import { formatOrgTime } from '@/utils/dateUtils';
import type { DayRow } from './dayRow';

/** One drawn segment, as percentages of the shared day track. */
export interface MicroBarSegment {
  offsetPct: number;
  widthPct: number;
}

export interface DayMicroBar {
  /** The planned window. Absent when the row has no planned child window. */
  planned?: MicroBarSegment;
  /** The actual attendance. Absent on an unrecorded or absent-marked day. */
  actual?: MicroBarSegment;
}

/** The shared day window all rows are drawn against, in minutes since midnight. */
export interface DayWindow {
  startMinutes: number;
  endMinutes: number;
}

/** A sane default window when no row carries any time at all: 07:00–19:00. */
const DEFAULT_WINDOW: DayWindow = { startMinutes: 7 * 60, endMinutes: 19 * 60 };

/** "HH:MM" (optionally with seconds) → minutes since midnight; null if unparseable. */
function hhmmToMinutes(value: string | undefined | null): number | null {
  if (!value) return null;
  const match = value.trim().match(/^(\d{2}):(\d{2})(?::(\d{2}))?$/);
  if (!match) return null;
  const hours = Number(match[1]);
  const minutes = Number(match[2]);
  if (hours > 23 || minutes > 59) return null;
  return hours * 60 + minutes;
}

/**
 * Reduce an ACTUAL time to org wall-clock minutes.
 *
 * Actual times are TIMESTAMPTZ instants. They MUST go through `formatOrgTime`
 * (which formats in the org timezone) before any arithmetic — a raw browser-local
 * format shows a phantom ±60/±120 min offset on web-entered rows, and `Date`
 * subtraction breaks across DST. Same rule as `settlingInVariance.ts`.
 */
function actualIsoToMinutes(iso: string | undefined | null): number | null {
  if (!iso) return null;
  return hhmmToMinutes(formatOrgTime(iso));
}

/**
 * Derive the shared day window across all rows: the earliest planned/actual
 * start to the latest end, so every row's bar is drawn on the same timeline and
 * the bars are comparable down the column.
 *
 * Falls back to a sane default when no row carries any time.
 */
export function deriveDayWindow(rows: DayRow[]): DayWindow {
  let min = Number.POSITIVE_INFINITY;
  let max = Number.NEGATIVE_INFINITY;

  for (const row of rows ?? []) {
    const candidates = [
      hhmmToMinutes(row.planned_child_present_from),
      hhmmToMinutes(row.planned_child_present_to),
      actualIsoToMinutes(row.actual_child_present_from),
      actualIsoToMinutes(row.actual_child_present_to),
    ];
    for (const value of candidates) {
      if (value === null) continue;
      if (value < min) min = value;
      if (value > max) max = value;
    }
  }

  if (!Number.isFinite(min) || !Number.isFinite(max) || max <= min) {
    return DEFAULT_WINDOW;
  }
  return { startMinutes: min, endMinutes: max };
}

/** Clamp a segment to the window and express it as offset/width percentages. */
function toSegment(
  fromMinutes: number,
  toMinutes: number,
  window: DayWindow,
): MicroBarSegment | undefined {
  const span = window.endMinutes - window.startMinutes;
  if (span <= 0) return undefined;

  const start = Math.max(fromMinutes, window.startMinutes);
  const end = Math.min(toMinutes, window.endMinutes);
  if (end <= start) return undefined;

  return {
    offsetPct: ((start - window.startMinutes) / span) * 100,
    widthPct: ((end - start) / span) * 100,
  };
}

/**
 * Geometry for a Days row's decorative micro-bar (ST-6/S20).
 *
 * Returns `{}` when neither side has a window, so the caller renders no bar at
 * all rather than a zero-width artifact; and leaves `actual` undefined on a day
 * with no tracking entry or an absent-marked day (null child-present window), so
 * the caller draws the planned segment alone rather than a half-drawn bar.
 *
 * The bar is DECORATIVE: the numeric times and the Variance column remain the
 * source of truth, and this adds no information the row does not already state.
 */
export function buildDayMicroBar(row: DayRow, window: DayWindow): DayMicroBar {
  const bar: DayMicroBar = {};

  const plannedFrom = hhmmToMinutes(row.planned_child_present_from);
  const plannedTo = hhmmToMinutes(row.planned_child_present_to);
  if (plannedFrom !== null && plannedTo !== null) {
    const segment = toSegment(plannedFrom, plannedTo, window);
    if (segment) bar.planned = segment;
  }

  const actualFrom = actualIsoToMinutes(row.actual_child_present_from);
  const actualTo = actualIsoToMinutes(row.actual_child_present_to);
  if (actualFrom !== null && actualTo !== null) {
    const segment = toSegment(actualFrom, actualTo, window);
    if (segment) bar.actual = segment;
  }

  return bar;
}
